export interface PricingCoreParams<TInsuranceTier extends string> {
  dailyRateCents: number
  days: number
  insuranceTier: TInsuranceTier
  promoDiscountCents?: number
  isSuperhost?: boolean
  /**
   * Optional host-set security deposit (the vehicle's `security_deposit_cents`).
   * It is a refundable HOLD, not revenue: it is added to `totalCents` (the guest
   * is charged it) but is NOT part of `preTax`, so it is never taxed, and it is
   * returned to the guest in full after the trip.
   */
  depositCents?: number
  /**
   * The vehicle's quoted Qualitas daily premium (`coverage_daily_rate_cents`).
   * When present it WINS over the flat `insuranceRates` constant: the premium
   * is the insurer's real quote per vehicle, passed through 1:1. When absent,
   * the flat constant is the fail-open fallback so a booking never breaks
   * while a vehicle awaits its first quote.
   */
  insuranceDailyRateCents?: number | null
  /**
   * Pair-repeat take rate. When omitted, `config.guestServiceFeePct` (or the
   * superhost pct) applies. Host payout is never computed from this number.
   */
  guestServiceFeePct?: number
}

export interface PricingCoreConfig<TInsuranceTier extends string> {
  platformInsuranceEnabled: boolean
  insuranceRates: Record<TInsuranceTier, { dailyCents: number }>
  guestServiceFeePct: number
  superhostFeePct: number
  ivaPct: number
  /**
   * Payment-processing fee % that the HOST absorbs on the deposit (Wompi's rate).
   * The guest is returned the deposit in full; this % of the deposit is deducted
   * from the host's earnings, since the deposit protects the host's own vehicle.
   */
  depositProcessingFeePct: number
  /**
   * Payment-processing fee % grossed up ON TOP of Tago's fee, paid by the guest,
   * so Tago nets 100% of its take (Wompi's effective rate, IVA-incl).
   */
  guestServiceFeeProcessingPct: number
}

export interface TripPriceCoreBreakdown {
  subtotalCents: number
  insuranceCents: number
  guestServiceFeeCents: number
  promoDiscountCents: number
  taxCents: number
  /**
   * Refundable security deposit added to the charge (untaxed). Optional so
   * display mappers over legacy/depositless rentals need not supply it —
   * calculateTripPriceCore always populates it (0 when there is no deposit).
   */
  depositCents?: number
  /** Deposit processing fee deducted from the host's earnings (host-internal). */
  depositProcessingFeeCents?: number
  totalCents: number
  hostEarningsCents: number
  platformFeeCents: number
  /** Wompi processing uplift on Tago's fee, guest-paid, pass-through. */
  platformFeeProcessingCents: number
}

export const INDEPENDENCE_CAMPAIGN = {
  id: 'sv_independence_day_2026',
  code: 'PATRIA15_2026',
  targetDate: '2026-09-15',
  bookingWindowStart: '2026-09-02T00:00:00-06:00',
  bookingWindowEnd: '2026-09-16T23:59:59-06:00',
  timezone: 'America/El_Salvador',
} as const

const CAMPAIGN_DATE_KEY_RE = /^(\d{4}-\d{2}-\d{2})(?:$|T)/
const CAMPAIGN_TIMEZONE_OFFSET_RE = /(Z|[+-]\d{2}:?\d{2})$/i

function campaignDateKey(value: string): string | null {
  const trimmed = value.trim()
  const match = CAMPAIGN_DATE_KEY_RE.exec(trimmed)
  const localDateKey = match?.[1]
  if (!localDateKey) return null

  if (!CAMPAIGN_TIMEZONE_OFFSET_RE.test(trimmed)) return localDateKey

  const date = new Date(trimmed)
  if (!Number.isFinite(date.getTime())) return null

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: INDEPENDENCE_CAMPAIGN.timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date)
  const year = parts.find((part) => part.type === 'year')?.value
  const month = parts.find((part) => part.type === 'month')?.value
  const day = parts.find((part) => part.type === 'day')?.value
  if (!year || !month || !day) return null
  return `${year}-${month}-${day}`
}

export function isIndependenceCampaignCode(code: string | null | undefined): boolean {
  return code?.trim().toUpperCase() === INDEPENDENCE_CAMPAIGN.code
}

export function isIndependenceCampaignBookingWindowOpen(now = new Date()): boolean {
  const currentTime = now.getTime()
  return (
    Number.isFinite(currentTime) &&
    currentTime >= new Date(INDEPENDENCE_CAMPAIGN.bookingWindowStart).getTime() &&
    currentTime <= new Date(INDEPENDENCE_CAMPAIGN.bookingWindowEnd).getTime()
  )
}

export function bookingTouchesIndependenceDate(params: {
  startAt: string
  endAt: string
}): boolean {
  const startKey = campaignDateKey(params.startAt)
  const endKey = campaignDateKey(params.endAt)
  if (!startKey || !endKey) return false

  const firstKey = startKey <= endKey ? startKey : endKey
  const lastKey = startKey <= endKey ? endKey : startKey
  return firstKey <= INDEPENDENCE_CAMPAIGN.targetDate && lastKey >= INDEPENDENCE_CAMPAIGN.targetDate
}

export function calculateIndependenceCampaignDiscountCents(params: {
  startAt: string
  endAt: string
  serviceFeeCents: number
  now?: Date
}): number {
  if (!isIndependenceCampaignBookingWindowOpen(params.now)) return 0
  if (!bookingTouchesIndependenceDate({ startAt: params.startAt, endAt: params.endAt })) return 0
  if (!Number.isFinite(params.serviceFeeCents)) return 0
  return Math.max(0, Math.round(params.serviceFeeCents))
}

export function calculateTripPriceCore<TInsuranceTier extends string>(
  params: PricingCoreParams<TInsuranceTier>,
  config: PricingCoreConfig<TInsuranceTier>,
): TripPriceCoreBreakdown {
  const {
    dailyRateCents,
    days,
    insuranceTier,
    promoDiscountCents = 0,
    isSuperhost = false,
  } = params

  const subtotalCents = dailyRateCents * days
  // Premium: the vehicle's real quoted daily rate when present, else the flat
  // constant fallback. Pass-through to the insurer, never Tago revenue.
  const insuranceDailyRateCents =
    params.insuranceDailyRateCents ?? config.insuranceRates[insuranceTier].dailyCents
  const insuranceCents = config.platformInsuranceEnabled ? insuranceDailyRateCents * days : 0
  // Guest-side fee — never a deduction from the host payout (hostEarningsCents
  // below stays the full subtotal). PLATFORM_FEES.hostFeePct does not exist in
  // this config on purpose.
  const guestFeePct =
    params.guestServiceFeePct ?? (isSuperhost ? config.superhostFeePct : config.guestServiceFeePct)
  const platformFeeCents = Math.round((subtotalCents * guestFeePct) / 100)
  // Wompi's processing cost is grossed up ON TOP of Tago's fee: the guest pays
  // the 15% plus its processing, so Tago nets 100% of its fee (DECIDED
  // 2026-09-06). This uplift is a pass-through, not Tago revenue — Tago's
  // invoice (DTE) covers platformFeeCents only, exactly like the insurance
  // premium passes through untouched.
  const platformFeeProcessingCents = Math.round(
    (platformFeeCents * config.guestServiceFeeProcessingPct) / 100,
  )
  const guestServiceFeeCents = platformFeeCents + platformFeeProcessingCents

  const preTaxCents = subtotalCents + guestServiceFeeCents + insuranceCents - promoDiscountCents

  const taxCents = Math.round((preTaxCents * config.ivaPct) / 100)

  // Security deposit: a refundable hold, NOT revenue. Added to the charged total
  // but excluded from preTax/tax (never taxed). The host absorbs its processing
  // fee, deducted from earnings.
  const depositCents = Math.max(0, Math.round(params.depositCents ?? 0))
  const depositProcessingFeeCents =
    depositCents > 0 ? Math.round((depositCents * config.depositProcessingFeePct) / 100) : 0

  const totalCents = preTaxCents + taxCents + depositCents
  const hostEarningsCents = subtotalCents - depositProcessingFeeCents

  return {
    subtotalCents,
    insuranceCents,
    guestServiceFeeCents,
    platformFeeCents,
    platformFeeProcessingCents,
    promoDiscountCents,
    taxCents,
    depositCents,
    depositProcessingFeeCents,
    totalCents,
    hostEarningsCents,
  }
}

export interface RefundParams {
  totalCents: number
  guestServiceFeeCents: number
  insuranceCents: number
  /**
   * The refundable security deposit that is part of totalCents. It is ALWAYS
   * returned in full and NEVER subject to the cancellation tiers — it is the
   * guest's own money held as a hold, not a rental charge. Omit/0 when there is
   * no deposit. (The host/platform-cancel and retracto branches already refund
   * totalCents in full, so the deposit rides along there; this field only
   * matters for the tiered guest-cancel branches, which must not penalize it.)
   */
  depositCents?: number
  hoursBeforeStart: number
  daysUsed: number
  totalDays: number
  /**
   * Who cancelled. Guest cancellations follow the time tiers and never get
   * the service fee back. Host/platform cancellations refund the guest 100%
   * of the charge, fee included — the guest is not at fault. Defaults to
   * 'guest' (the conservative preview for guest-facing screens).
   */
  cancelledBy?: 'guest' | 'host' | 'platform' | 'system'
  /**
   * Hours since the booking/contract was created. Drives the mandatory
   * El Salvador derecho de retracto (LPC Art. 13-A): a guest who cancels within
   * 8 days of booking AND before the trip starts is owed a FULL refund incl.
   * the service fee, overriding the fee-retaining tiers. Omit (undefined) to
   * skip the retracto check (falls back to the tiers) — but callers with the
   * booking timestamp SHOULD pass it so the legal right is honored.
   */
  hoursSinceBooking?: number
}

export interface RefundBreakdown {
  refundCents: number
  policy: 'full' | 'partial' | 'none'
}

/** El Salvador LPC Art. 13-A derecho de retracto: 8-day cooling-off window. */
export const RETRACTO_WINDOW_HOURS = 8 * 24

export function calculateRefundCore(params: RefundParams): RefundBreakdown {
  const {
    totalCents,
    guestServiceFeeCents,
    insuranceCents,
    hoursBeforeStart,
    daysUsed,
    totalDays,
    cancelledBy = 'guest',
    hoursSinceBooking,
  } = params

  // The security deposit is the guest's own money and is returned in full on any
  // cancellation, never touched by the time tiers.
  const depositCents = Math.max(0, params.depositCents ?? 0)

  // Host- or Tago-initiated cancellation: the guest gets 100% back, service
  // fee included — the guest is not at fault. totalCents already carries the
  // deposit, so it is returned here too.
  if (cancelledBy === 'host' || cancelledBy === 'platform') {
    return { refundCents: totalCents, policy: 'full' }
  }

  // MANDATORY consumer right — El Salvador Ley de Protección al Consumidor
  // Art. 13-A (derecho de retracto for distance / e-commerce contracts):
  // a guest who cancels within 8 days of BOOKING and BEFORE the trip starts is
  // owed a FULL refund of everything, INCLUDING the otherwise-non-refundable
  // service fee. Any clause penalizing this right is void (Art. 13-A lit.
  // b-III), so it overrides the time tiers below. It does NOT apply once the
  // trip has started or after the 8-day window — there the fee-retaining tiers
  // stand and Tago's fee stays non-refundable.
  const withinRetracto =
    hoursSinceBooking !== undefined &&
    hoursSinceBooking <= RETRACTO_WINDOW_HOURS &&
    hoursBeforeStart > 0
  if (withinRetracto) {
    return { refundCents: totalCents, policy: 'full' }
  }

  // Guest (or system) cancellation outside the retracto window: Tago's service
  // fee is NOT refunded — company policy (2026-08-15) and the published ToS.
  // Only the rental portion and the unused insurance fraction are refundable,
  // per time tier.
  // The deposit is carved out of the tiered base and added back in full below,
  // so a late cancel never keeps a cent of it.
  const rentalOnlyCents = Math.max(
    0,
    totalCents - insuranceCents - guestServiceFeeCents - depositCents,
  )
  const unusedFraction = totalDays > 0 ? (totalDays - daysUsed) / totalDays : 0
  const unusedInsuranceCents = Math.round(insuranceCents * unusedFraction)

  if (hoursBeforeStart > 48) {
    return {
      refundCents: rentalOnlyCents + unusedInsuranceCents + depositCents,
      policy: 'full',
    }
  }
  if (hoursBeforeStart >= 24) {
    return {
      refundCents: Math.round(rentalOnlyCents * 0.5) + unusedInsuranceCents + depositCents,
      policy: 'partial',
    }
  }
  return { refundCents: unusedInsuranceCents + depositCents, policy: 'none' }
}

export interface LateReturnConfig {
  gracePeriodMinutes: number
  tier1PctOfDaily: number
  tier2PctOfDaily: number
  maxLateDays: number
}

export function calculateLateFeeCore(
  dailyRateCents: number,
  minutesLate: number,
  config: LateReturnConfig,
): number {
  if (minutesLate <= config.gracePeriodMinutes) return 0
  if (minutesLate <= 120) return Math.round((dailyRateCents * config.tier1PctOfDaily) / 100)
  if (minutesLate <= 1440) return Math.round((dailyRateCents * config.tier2PctOfDaily) / 100)
  const extraDays = Math.min(Math.ceil(minutesLate / 1440), config.maxLateDays)
  return dailyRateCents * extraDays
}

export function calculateExtraKmCharge(extraKm: number, rateCentsPerKm: number): number {
  if (extraKm <= 0) return 0
  return Math.round(extraKm * rateCentsPerKm)
}
